/**
 * Helpers para meta tags SEO: truncamento, URLs de produção e inferência de imagem OG.
 */

const DEFAULT_OG_WIDTH = 1200;
const DEFAULT_OG_HEIGHT = 630;

export function truncateSeoTitle(title, maxLength = 60) {
  if (!title || typeof title !== 'string') return title || '';
  const trimmed = title.trim();
  if (trimmed.length <= maxLength) return trimmed;

  let cut = trimmed.substring(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > Math.floor(maxLength * 0.55)) {
    cut = cut.substring(0, lastSpace);
  }
  return cut.replace(/[\s|\-,–—:;]+$/, '').trim();
}

export function truncateSeoDescription(description, maxLength = 160) {
  if (!description || typeof description !== 'string') return description || '';
  const plain = description.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim();
  if (plain.length <= maxLength) return plain;

  let cut = plain.substring(0, maxLength);
  const lastSpace = cut.lastIndexOf(' ');
  if (lastSpace > Math.floor(maxLength * 0.65)) {
    cut = cut.substring(0, lastSpace);
  }
  return `${cut.replace(/[,;:\s]+$/, '').trim()}…`;
}

export function resolveOfficialBaseUrl(settings) {
  const fromSettings = settings?.seoCanonicalUrl?.trim();
  if (fromSettings && !fromSettings.includes('localhost') && !fromSettings.includes('127.0.0.1')) {
    return fromSettings.replace(/\/$/, '').replace(/^http:\/\//i, 'https://');
  }
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (!origin.includes('localhost') && !origin.includes('127.0.0.1')) {
      return origin.replace(/^http:\/\//i, 'https://');
    }
  }
  return '';
}

export function normalizeProductionUrl(url, officialBaseUrl) {
  const base = (officialBaseUrl || '').replace(/\/$/, '');
  if (!url) return base ? `${base}/` : '';

  let normalized = String(url).trim();

  if (normalized.includes('localhost') || normalized.includes('127.0.0.1')) {
    const path = normalized.replace(/^https?:\/\/[^/]+/, '') || '/';
    normalized = `${base}${path.startsWith('/') ? path : `/${path}`}`;
  } else if (normalized.startsWith('/')) {
    normalized = `${base}${normalized}`;
  } else {
    normalized = normalized.replace(/^http:\/\//i, 'https://');
    if (!normalized.startsWith('https://') && !normalized.startsWith('http://')) {
      normalized = `${base}/${normalized.replace(/^\//, '')}`;
    }
  }

  try {
    const parsed = new URL(normalized);
    const isHome = parsed.pathname === '/' || parsed.pathname === '';
    if (isHome) {
      return `${parsed.origin}/`;
    }
    const path = parsed.pathname.replace(/\/+$/, '') || '/';
    return `${parsed.origin}${path}${parsed.search}${parsed.hash}`;
  } catch {
    return normalized;
  }
}

export function inferOgImageType(imageUrl) {
  if (!imageUrl) return 'image/jpeg';
  const lower = String(imageUrl).toLowerCase().split('?')[0];
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.avif')) return 'image/avif';
  if (lower.endsWith('.svg')) return 'image/svg+xml';
  return 'image/jpeg';
}

export function getOgImageDimensions(_imageUrl) {
  return { width: DEFAULT_OG_WIDTH, height: DEFAULT_OG_HEIGHT };
}

export function stripUndefinedDeep(value) {
  if (Array.isArray(value)) {
    return value
      .map(stripUndefinedDeep)
      .filter((item) => item !== undefined && item !== null && item !== '');
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, val]) => {
      const cleaned = stripUndefinedDeep(val);
      if (
        cleaned !== undefined &&
        cleaned !== null &&
        cleaned !== '' &&
        !(typeof cleaned === 'object' && !Array.isArray(cleaned) && Object.keys(cleaned).length === 0)
      ) {
        acc[key] = cleaned;
      }
      return acc;
    }, {});
  }
  return value;
}

export function toSchemaIsoDate(value) {
  if (!value) return undefined;
  if (typeof value.toDate === 'function') return value.toDate().toISOString();
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'object' && typeof value.seconds === 'number') {
    return new Date(value.seconds * 1000).toISOString();
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

/**
 * Meta description dinâmica para página de imóvel quando seo.metaDescription não existe.
 */
export function buildDynamicPropertyDescription(property, companyName) {
  if (!property) return '';

  if (property.seo?.metaDescription?.trim()) {
    return truncateSeoDescription(property.seo.metaDescription);
  }

  if (property.description) {
    return truncateSeoDescription(property.description);
  }

  const purpose = property.purpose === 'rent' ? 'para alugar' : 'à venda';
  const type = property.type || 'Imóvel';
  const location = [property.neighborhood, property.city].filter(Boolean).join(', ');
  const rooms = property.bedrooms ? `${property.bedrooms} quartos` : '';
  const price = property.purpose === 'rent' ? property.rent : property.price;
  const priceText = price
    ? ` ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(price)}`
    : '';

  const parts = [
    `${type} ${purpose}${priceText ? ` por${priceText}` : ''}`,
    rooms,
    location ? `em ${location}` : '',
    companyName ? `— ${companyName}` : '',
  ].filter(Boolean);

  return truncateSeoDescription(parts.join('. ').replace(/\.\s*\./g, '.'));
}

/** Imagem OG efetiva da home: logo por padrão, com fallbacks. */
export function resolveHomeOgImage(settings = {}) {
  const useLogo = settings.seoUseLogoForOgImage !== false;
  const logo = settings.logo || settings.logoDark || null;
  const header = settings.homeHeaderImage || null;
  const custom = settings.seoOgImage || null;

  if (!useLogo) {
    return custom || header || logo;
  }
  return logo || header || custom;
}

function inferServiceAreaFromContact(settings = {}) {
  const structured = settings.contactAddressStructured;
  if (structured?.localidade && structured?.uf) {
    return `${structured.localidade}, ${structured.uf}`;
  }
  if (settings.contactCidade && settings.contactEstado) {
    return `${settings.contactCidade}, ${settings.contactEstado}`;
  }
  if (settings.contactCidade) return settings.contactCidade;
  return '';
}

/** Preenche campos SEO vazios da home com dados já cadastrados (no save). */
export function enrichHomeSeoOnSave(settings = {}) {
  const baseUrl = resolveOfficialBaseUrl(settings);
  const brand = settings.companyName?.trim() || '';
  const autoMetaTitle = brand ? `${brand} - Imóveis | Compra, Venda e Aluguel` : '';
  const autoMetaDescription = truncateSeoDescription(
    settings.companyDescription ||
      settings.aboutHeroDescription?.replace?.(/<[^>]*>/g, ' ') ||
      ''
  );
  const metaTitle = settings.seoMetaTitle?.trim() || autoMetaTitle;
  const metaDescription = settings.seoMetaDescription?.trim() || autoMetaDescription;
  const serviceArea = settings.seoServiceArea?.trim() || inferServiceAreaFromContact(settings);

  return {
    ...settings,
    seoMetaTitle: metaTitle,
    seoMetaDescription: metaDescription,
    seoCanonicalUrl: settings.seoCanonicalUrl?.trim() || baseUrl,
    seoOgTitle: settings.seoOgTitle?.trim() || metaTitle,
    seoOgDescription: settings.seoOgDescription?.trim() || metaDescription,
    seoUseLogoForOgImage: settings.seoUseLogoForOgImage !== false,
    seoBusinessType: settings.seoBusinessType || 'RealEstateAgent',
    seoServiceArea: serviceArea,
  };
}
